import {SimpleEditor} from "../SimpleEditor.js";
import {Temp} from "../../utils/Temp.js";
import {Listenable} from "../../forms/Listenable.js";

class BoolFlag extends Listenable {
	#value;

	constructor(bool = false) {
		super();
		this.#value = bool;
	}

	get state() {
		return this.#value;
	}

	set state(bool) {
		const changed = this.#value !== bool;
		this.#value = bool;
		if (changed) this.fire("change", bool);
	}
}

class ConstantBoolFlag extends BoolFlag {
	constructor(bool) {
		super(bool);
	}

	get state() { return super.state; }
	set state(bool) { /* not modifiable */ }
}

const TRUE_FLAG = new ConstantBoolFlag(true);
const FALSE_FLAG = new ConstantBoolFlag(false);


export class HtmlEditor extends SimpleEditor {
	constructor(parent, fieldPath, obj, member, widgetOptions) {
		super(parent, fieldPath, obj, member, widgetOptions);
		this.closeTimeout = 0;
		this.clickableLinks = new BoolFlag(!!widgetOptions.clickableLinks);
		this.clickableLinks.on("change", this.updateLinksEditability, this);
	}

	rebuild() {
		this.widget = this.root = this.makeWidget();
		this.updateScreen();
		this.widget.on('keyup', function (e) {
			this.updateData();
		}.bind(this));
		this.toolbar = new Toolbar(this, this.clickableLinks);

		this.widget.on("focus", function(e) {
			if (this.closeTimeout !== 0) {
				window.clearTimeout(this.closeTimeout);
				this.closeTimeout = 0;
			} else this.toolbar.show();
		}.bind(this));

		this.widget.on("blur", function(e) {
			this.closeTimeout = window.setTimeout(()=> {
				this.closeTimeout = 0;
				this.toolbar.hide();
				this.clickableLinks = new BoolFlag(this.clickableLinks.state);	// to deregister all listeners
				this.clickableLinks.on("change", this.updateLinksEditability, this);
			}, 200);
		}.bind(this));

	}

	makeWidget() {
		const w = Temp.tag('div', this.options);
		if (!this.options.readOnly) {
			w.attr('contenteditable', 'true');
			w.addClass('likeInput');
		}
		w.css("white-space", "normal");
		return w;
	}

	enableWidget(enable) {
		if (enable) {
			this.widget.attr('contenteditable', 'true');
			this.widget.addClass('likeInput');
		} else {
			this.widget.removeAttr('contenteditable');
			this.widget.removeClass('likeInput');
		}
	}

	updateScreen() {
		if (this.widget != null) {
			this.widget.html(this.fromData(this.data));
			this.updateLinksEditability();
		}
	}

	updateLinksEditability() {
		this.updateLinksEditabilityWith(this.clickableLinks.state);
	}

	updateLinksEditabilityWith(clickable) {
		if (clickable) {
			this.widget.find("a").each(function() {
				$(this).prop("contenteditable", false);
			});
		} else {
			this.widget.find("a").each(function() {
				$(this).removeAttr("contenteditable");
			});
		}
	}

	updateData() {
		if (this.widget != null) {
			// remove contenteditable from links
			if (this.clickableLinks.state) this.updateLinksEditabilityWith(false);	// remove contenteditable from links
			this.data = this.toData(this.widget.html());
			if (this.clickableLinks.state) this.updateLinksEditability();	// add contenteditable back
		}
	}

	showToolbar() {

	}
}


class ToolbarButton {
	/**
	 * @param {string} name name of the button
	 * @param {string} icon name of anneca symbol
	 * @param {string} label
	 * @param {function} action invoked when the button is clicked
	 * @param {BoolFlag} enabled
	 */
	constructor(name, icon, label, action, enabled = TRUE_FLAG) {
		this.name = name;
		this.root = $("<button type='button' class='toolbar-button'></button>");
		this.root.on("click", (e) => {
			e.preventDefault();
			action();
		});
		if (label) this.root.attr("title", label);
		this.root.append(Icon.fi(icon));
		if (!enabled.state) this.root.prop("disabled", true);
		enabled.on("change", this.enabledChanged, this);
	}

	get ui() {
		return this.root;
	}

	enabledChanged(source, newState) {
		this.root.prop("disabled", !newState);
	}
}

class ToolbarSwitchButton extends ToolbarButton {

	constructor(name, icon, label, enabled = TRUE_FLAG, switched = FALSE_FLAG, focusWidget) {
		super(name, icon, label, ()=>{
			this.toggleMe();
			focusWidget.focus();
		}, enabled);
		this.switched = switched;
		if (switched.state) this.root.addClass("switched-on");
		switched.on("change", (source, st)=> {
			if (st) this.root.addClass("switched-on");
			else this.root.removeClass("switched-on");
		})
	}

	toggleMe() {
		this.switched.state = !this.switched.state;
	}
}

class ToolbarCommandButton extends ToolbarButton {

	/**
	 *
	 * @param {string} browserCommand
	 * @param {?string} askForArgument
	 */
	constructor(name, icon, label, browserCommand, enabled = TRUE_FLAG, askForArgument = null, askWithDefault="") {
		super(name, icon, label, ()=>{
			let arg = undefined;
			if (askForArgument) {
				arg = prompt(askForArgument, askWithDefault);
			}
			document.execCommand(browserCommand, false, arg);
		}, enabled);
		this.browserCommadn = browserCommand;
	}
}

class Toolbar {
	constructor(editor, clickableLinks) {
		this.toolbar = null;
		this.clickableLinks = clickableLinks;
		this.editor = editor;
		this.widget = editor.widget;
	}

	show() {
		if (this.toolbar === null) {
			this.toolbar = $("<div class='html-toolbar'></div>");
			const o = this.widget.offset();
			this.toolbar.css("left", o.left + "px");
			this.toolbar.css("top", (o.top-29) + "px");

			this.appendCommand("bold", "edit-bold");
			this.appendCommand("italic", "edit-italic");
			this.appendCommand("underline", "edit-underline");
			this.appendCommand("strikeThrough", "edit-strike");
			this.appendSeparator();
			this.appendCommand("insertOrderedList", "edit-ol");
			this.appendCommand("insertUnorderedList", "edit-ul");
			this.appendSeparator();
			this.appendPromptCommand("createLink", "edit-link", "Link", "https://www.");
			this.appendCommand("unlink", "edit-unlink");
			this.appendToggleSwitch("clickable", "click", this.clickableLinks);

			$("body").append(this.toolbar);
		}
	}

	appendSeparator() {
		const s = $("<span></span>");
		this.toolbar.append(s);
	}

	appendCommand(command, icon) {
		const item = new ToolbarCommandButton(command, icon, null, command, TRUE_FLAG);
		this.toolbar.append(item.ui);
	}

	appendPromptCommand(command, icon, prompt, defaultValue) {
		const item = new ToolbarCommandButton(command, icon, null, command, TRUE_FLAG, prompt, defaultValue);
		this.toolbar.append(item.ui);
	}

	appendToggleSwitch(name, icon, sw) {
		const item = new ToolbarSwitchButton(name, icon, null, TRUE_FLAG, sw, this.widget);
		this.toolbar.append(item.ui);
	}

	hide() {
		if (this.toolbar != null) {
			this.toolbar.remove();
			this.toolbar = null;
		}
	}
}
